import { SUPABASE_URL, SUPABASE_KEY } from './supabaseClient'

/**
 * Low-level authenticated fetch against the Supabase REST API.
 * Constructs the full URL from `path`, injects `apikey` and `Authorization`
 * headers, and merges any additional fetch options.
 *
 * @param {string} path - REST path appended to `SUPABASE_URL/rest/v1/`
 *   (e.g., `"projects?select=*"` or `"tasks?project_id=eq.abc"`).
 * @param {string} token - Supabase access token (JWT from `session.access_token`).
 * @param {RequestInit} [options={}] - Additional fetch options. `options.headers`
 *   are merged with (and can override) the default `apikey`/`Authorization` headers.
 * @returns {Promise<Response>} The raw fetch `Response` object.
 */
export async function supabaseFetchWithToken(path, token, options = {}) {
  const url = `${SUPABASE_URL}/rest/v1/${path}`
  const headers = {
    apikey: SUPABASE_KEY,
    Authorization: `Bearer ${token}`,
    ...options.headers,
  }
  const res = await fetch(url, { ...options, headers })
  return res
}

/**
 * Authenticated GET request against the Supabase REST API.
 * Returns a `{ data, error }` tuple — never throws on HTTP errors.
 *
 * @param {string} path - REST path appended to `SUPABASE_URL/rest/v1/`
 *   (e.g., `"project_members?select=*&project_id=eq.abc"`).
 * @param {string} token - Supabase access token (JWT).
 * @returns {Promise<{ data: any[] | null, error: { status: number, message: string } | null }>}
 *   On success: `{ data: <parsed JSON>, error: null }`.
 *   On failure: `{ data: null, error: { status, message } }`.
 */
export async function supabaseGet(path, token) {
  const res = await supabaseFetchWithToken(path, token)
  if (!res.ok) {
    const message = await res.text().catch(() => res.statusText)
    console.error(`supabaseGet ${res.status}: ${path}`, message)
    return { data: null, error: { status: res.status, message } }
  }
  const data = await res.json()
  return { data, error: null }
}

/**
 * Authenticated mutating request (POST/PATCH/DELETE) against the Supabase REST API.
 * Sets `Content-Type: application/json` and `Prefer` header automatically.
 *
 * @param {string} path - REST path appended to `SUPABASE_URL/rest/v1/`.
 * @param {string} token - Supabase access token (JWT).
 * @param {Object} [options={}]
 * @param {'POST' | 'PATCH' | 'DELETE'} [options.method='POST'] - HTTP method.
 * @param {Record<string, any>} [options.body] - Request body (will be JSON-serialized).
 * @param {string} [options.prefer='return=minimal'] - PostgREST `Prefer` header
 *   (e.g., `'return=representation'` to get the created/updated row back).
 * @returns {Promise<Response>} The raw fetch `Response` object.
 */
export async function supabaseMutate(path, token, { method = 'POST', body, prefer = 'return=minimal' } = {}) {
  const res = await supabaseFetchWithToken(path, token, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Prefer: prefer,
    },
    body: JSON.stringify(body),
  })
  return res
}

/**
 * Invoke a Supabase Edge Function by name. Sends a POST with JSON body
 * and an AbortController-based timeout. All Edge Functions are called
 * with `--no-verify-jwt` and validate JWTs internally.
 *
 * @param {string} functionName - Edge Function name (e.g., `"nl-query"`,
 *   `"generate-template"`, `"create-checkout"`).
 * @param {string} token - Supabase access token (JWT).
 * @param {Record<string, any>} [body={}] - Request payload (JSON-serialized).
 * @param {Object} [options={}]
 * @param {number} [options.timeoutMs=30000] - Abort the request after this
 *   many milliseconds. Throws `AbortError` on timeout.
 * @returns {Promise<Response>} The raw fetch `Response` object.
 *   Caller is responsible for checking `res.ok` and parsing `res.json()`.
 */
export async function invokeFunction(functionName, token, body = {}, { timeoutMs = 30000 } = {}) {
  const url = `${SUPABASE_URL}/functions/v1/${functionName}`
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
    return res
  } finally {
    clearTimeout(timer)
  }
}
